/**
 * INVENTORY MANAGER
 * 
 * FIX for QuotaExceededError: 
 * - Stores ONLY card metadata (id, rarity, power, name)
 * - Does NOT store Base64 image data
 * - Images are loaded from server/assets based on card ID
 */

const INVENTORY_KEY = 'user_inventory_v2';
const OLD_INVENTORY_KEY = 'user_inventory';
const BACKUP_KEY = 'userCards_backup';

/**
 * Clean corrupted storage on first load
 * Removes old bloated inventory data
 */
export const cleanupStorage = () => {
  try {
    // Remove old backup that causes QuotaExceededError
    localStorage.removeItem(BACKUP_KEY);
    localStorage.removeItem('userCards_backup_old');
    
    // Check if we need to migrate old inventory
    const oldInventory = localStorage.getItem(OLD_INVENTORY_KEY);
    const newInventory = localStorage.getItem(INVENTORY_KEY);
    
    if (oldInventory && !newInventory) {
      try {
        const cards = JSON.parse(oldInventory);
        // Migrate to lightweight format
        const lightweightCards = cards.map(card => ({
          id: card.id || `card_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`,
          character_name: card.character_name,
          title: card.title,
          rarity: card.rarity,
          power_rating: card.power_rating,
          card_class: card.card_class,
          acquired_at: card.acquired_at || Date.now(),
          // IMPORTANT: Do NOT include image_base64
        }));
        
        localStorage.setItem(INVENTORY_KEY, JSON.stringify(lightweightCards));
        // Remove old bloated inventory
        localStorage.removeItem(OLD_INVENTORY_KEY);
        console.log('[InventoryManager] Migrated', lightweightCards.length, 'cards to lightweight format');
      } catch (e) {
        console.error('[InventoryManager] Migration failed:', e);
        localStorage.removeItem(OLD_INVENTORY_KEY);
      }
    }
    
    // Check storage usage
    const totalSize = Object.keys(localStorage).reduce((total, key) => {
      return total + (localStorage.getItem(key)?.length || 0);
    }, 0);
    
    console.log('[InventoryManager] Storage usage:', Math.round(totalSize / 1024), 'KB');
    
    // If still too large (>4MB), clear inventory
    if (totalSize > 4 * 1024 * 1024) {
      console.warn('[InventoryManager] Storage too large, clearing inventory');
      localStorage.removeItem(INVENTORY_KEY);
      localStorage.removeItem(OLD_INVENTORY_KEY);
    }
    
    return true;
  } catch (e) {
    console.error('[InventoryManager] Cleanup error:', e);
    return false;
  }
};

/**
 * Get all cards from inventory (lightweight - no images)
 */
export const getInventory = () => {
  try {
    const data = localStorage.getItem(INVENTORY_KEY);
    if (!data) return [];
    return JSON.parse(data);
  } catch (e) {
    console.error('[InventoryManager] Failed to read inventory:', e);
    return [];
  }
};

/**
 * Save a card to inventory (metadata only, no image)
 */
export const saveCard = (card) => {
  try {
    const inventory = getInventory();
    
    // Create lightweight card (NO IMAGE)
    const lightweightCard = {
      id: card.id || `card_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`,
      character_name: card.character_name,
      title: card.title,
      rarity: card.rarity,
      power_rating: card.power_rating,
      card_class: card.card_class,
      acquired_at: card.acquired_at || Date.now(),
      // Explicit: No image_base64
    };
    
    // Check for duplicates
    const exists = inventory.some(c => c.id === lightweightCard.id);
    if (exists) {
      console.log('[InventoryManager] Card already exists:', lightweightCard.id);
      return false;
    }
    
    inventory.push(lightweightCard);
    localStorage.setItem(INVENTORY_KEY, JSON.stringify(inventory));
    console.log('[InventoryManager] Saved card:', lightweightCard.id);
    
    // Dispatch event for other components
    window.dispatchEvent(new CustomEvent('inventory-updated'));
    
    return true;
  } catch (e) {
    console.error('[InventoryManager] Failed to save card:', e);
    
    // If quota exceeded, try clearing and retrying
    if (e.name === 'QuotaExceededError') {
      console.warn('[InventoryManager] Quota exceeded, clearing old data');
      cleanupStorage();
      // Retry once
      try {
        const inventory = getInventory();
        const lightweightCard = {
          id: card.id || `card_${Date.now()}`,
          character_name: card.character_name,
          title: card.title,
          rarity: card.rarity,
          power_rating: card.power_rating,
          card_class: card.card_class,
          acquired_at: Date.now(),
        };
        inventory.push(lightweightCard);
        localStorage.setItem(INVENTORY_KEY, JSON.stringify(inventory));
        return true;
      } catch (e2) {
        console.error('[InventoryManager] Retry failed:', e2);
      }
    }
    return false;
  }
};

/**
 * Remove a card from inventory
 */
export const removeCard = (cardId) => {
  try {
    const inventory = getInventory();
    const filtered = inventory.filter(c => c.id !== cardId);
    localStorage.setItem(INVENTORY_KEY, JSON.stringify(filtered));
    window.dispatchEvent(new CustomEvent('inventory-updated'));
    return true;
  } catch (e) {
    console.error('[InventoryManager] Failed to remove card:', e);
    return false;
  }
};

/**
 * Clear entire inventory
 */
export const clearInventory = () => {
  localStorage.removeItem(INVENTORY_KEY);
  localStorage.removeItem(OLD_INVENTORY_KEY);
  window.dispatchEvent(new CustomEvent('inventory-updated'));
};

// Run cleanup on module load
cleanupStorage();

export default {
  getInventory,
  saveCard,
  removeCard,
  clearInventory,
  cleanupStorage,
};
